<template>
    <div class="fb-controls">
        <div class="fb-controls-grid">
            <div class="fb-field">
                <!-- aquacred exposes no namespace listing, so this is a combobox: known
                     namespaces are offered, any other can be typed -->
                <label class="fb-label">Namespace</label>
                <InputField type="customSingleSelect" :showLabel="false" :options="namespaceOptions"
                    :canClear="false" placeholder="default" v-model="selectedNamespace" />
            </div>
            <div class="fb-field">
                <label class="fb-label">Credential</label>
                <InputField type="customSingleSelect" :showLabel="false" :options="credentialOptions"
                    :createOption="false" :canClear="false" :editable="!!credentialOptions.length"
                    :placeholder="credentialPlaceholder" v-model="selectedCredential" />
            </div>
            <div class="fb-field">
                <label class="fb-label">Bucket</label>
                <InputField type="customSingleSelect" :showLabel="false" :options="bucketOptions"
                    :createOption="false" :canClear="false" :editable="!!bucketOptions.length"
                    :placeholder="bucketPlaceholder" v-model="selectedBucket" />
            </div>
            <button type="button" class="fb-icon-btn fb-controls-refresh" :disabled="busy" title="Refresh"
                @click="onRefresh">
                <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': busy }"></i>
            </button>
        </div>
        <div v-if="error" class="fb-notice fb-notice-danger mt-3">
            <i class="fa-solid fa-circle-exclamation"></i>
            <span>{{ error }}</span>
        </div>
        <div v-else-if="message" class="fb-notice mt-3">
            <i class="fa-solid fa-circle-info"></i>
            <span>{{ message }}</span>
        </div>
    </div>
</template>

<script>
/*
 * Namespace -> credential -> bucket picker shared by the file browser page and the file
 * selection modal. Credentials come from aquacred (service `filebrowser`); the chosen
 * one decides both what pail can reach and the URI scheme (s3:// vs gs://) every path
 * below it is displayed with.
 */
import { mapActions } from "vuex";
import InputField from "../InputField.vue";
import {
    FILE_BROWSER_CRED_SERVICE,
    FILE_BROWSER_CRED_NAMESPACE,
    cloudLabelForCredentialType,
    isBrowsableCredential,
    normalizeBuckets,
    schemeForCredentialType,
} from "../../utils/fileBrowser";

export default {
    name: "FileBrowserControls",
    components: { InputField },
    props: {
        // Pre-select these when present (deep links / page reloads)
        initialNamespace: { type: String, default: "" },
        initialCredential: { type: String, default: "" },
        initialBucket: { type: String, default: "" },
        // The page browser opens straight into a bucket; the modal waits for a pick
        autoSelectBucket: { type: Boolean, default: false },
    },
    data() {
        return {
            // namespaces the user has successfully loaded credentials from this session
            visitedNamespaces: [],
            credentialList: [],
            bucketList: [],
            selectedNamespace: null,
            selectedCredential: null,
            selectedBucket: null,
            dropDownLoading: "",
            error: null,
            message: null,
        };
    },
    computed: {
        namespaceOptions() {
            const seen = [FILE_BROWSER_CRED_NAMESPACE, this.initialNamespace, ...this.visitedNamespaces];
            // namespaces already read this session are offered alongside the default
            const cached = Object.keys(this.$store.state.amdp.aquacredCredentials?.[FILE_BROWSER_CRED_SERVICE] || {});
            return [...new Set([...seen, ...cached])].filter(Boolean).map((ns) => ({ label: ns, value: ns }));
        },
        credentialOptions() {
            // the cloud is part of the identity here - it decides which buckets appear
            return this.credentialList.map((cred) => {
                const cloud = cloudLabelForCredentialType(cred.type);
                return { label: cloud ? `${cred.name} · ${cloud}` : cred.name, value: cred.name };
            });
        },
        bucketOptions() {
            return this.bucketList.map((bucket) => ({ label: bucket.name, value: bucket.name }));
        },
        credentialPlaceholder() {
            if (this.dropDownLoading === "credential") return "Loading credentials…";
            return this.credentialOptions.length ? "Select a credential" : "No credentials available";
        },
        bucketPlaceholder() {
            if (this.dropDownLoading === "bucket") return "Loading buckets…";
            if (!this.selectedCredential) return "Choose a credential first";
            return this.bucketOptions.length ? "Select a bucket" : "No buckets available";
        },
        busy() {
            return this.dropDownLoading !== "";
        },
        currentCid() {
            return this.$store.state.currentClient?.cid;
        },
        selectedCredentialDetail() {
            return this.credentialList.find((cred) => cred.name === this.selectedCredential) || null;
        },
    },
    watch: {
        async selectedNamespace(namespace) {
            this.resetBelowNamespace();
            if (namespace) await this.fetchCredentials();
        },
        selectedCredential(name) {
            this.resetBelowCredential();
            if (!name) return;
            const detail = this.selectedCredentialDetail;
            this.$store.dispatch("amdp/setSelectedNamespace", this.selectedNamespace);
            this.$store.dispatch("amdp/setSelectedCredential", name);
            this.$store.dispatch("amdp/setSelectedCredentialType", detail?.type || null);
            this.$emit("credentialChange", {
                name,
                type: detail?.type || null,
                scheme: schemeForCredentialType(detail?.type),
                namespace: this.selectedNamespace,
            });
            this.fetchBuckets();
        },
        selectedBucket(bucket) {
            if (bucket) this.$emit("bucketChange", bucket);
        },
        currentCid(to, from) {
            // the page is reused across a CID switch - rebuild the whole selection
            if (to && from && to !== from) this.initSelection();
        },
    },
    methods: {
        ...mapActions({
            fetchAquacredCredentials: "amdp/fetchAquacredCredentials",
            getBucketList: "amdp/getBucketList",
        }),
        setError(error) {
            this.error = error?.response?.data?.error || error?.message || String(error);
        },
        clearNotices() {
            this.error = null;
            this.message = null;
        },
        resetBelowNamespace() {
            this.clearNotices();
            this.credentialList = [];
            this.selectedCredential = null;
            this.resetBelowCredential();
        },
        resetBelowCredential() {
            this.bucketList = [];
            this.selectedBucket = null;
            this.$store.dispatch("setBreadcrumbs", []);
            this.$store.dispatch("amdp/setFolderPath", "");
        },
        initSelection() {
            this.resetBelowNamespace();
            this.selectedNamespace = null;
            // assigning triggers the namespace watcher, which loads the credentials
            this.selectedNamespace = this.initialNamespace || FILE_BROWSER_CRED_NAMESPACE;
        },
        async fetchCredentials(forceGet = false) {
            this.dropDownLoading = "credential";
            this.clearNotices();
            let credentials = [];
            try {
                const response = await this.fetchAquacredCredentials({
                    service: FILE_BROWSER_CRED_SERVICE,
                    namespace: this.selectedNamespace,
                    mutation: "setAquacredCredentials",
                    forceGet,
                });
                // a cache hit resolves to the credential list, a fresh read to the response
                credentials = Array.isArray(response) ? response : this.credentialsFromStore();
            } catch (e) {
                this.setError(e);
                this.dropDownLoading = "";
                return;
            }
            // only object-store credentials can browse buckets
            this.credentialList = credentials.filter(isBrowsableCredential);
            this.dropDownLoading = "";
            if (!this.credentialList.length) {
                this.message = `No AWS or GCP credentials under namespace "${this.selectedNamespace}".`;
                return;
            }
            if (!this.visitedNamespaces.includes(this.selectedNamespace)) {
                this.visitedNamespaces.push(this.selectedNamespace);
            }
            this.selectedCredential = this.initialCredential && this.credentialList.some((c) => c.name === this.initialCredential)
                ? this.initialCredential
                : this.credentialList[0].name;
        },
        credentialsFromStore() {
            return this.$store.state.amdp.aquacredCredentials?.[FILE_BROWSER_CRED_SERVICE]?.[this.selectedNamespace]?.credentials || [];
        },
        async fetchBuckets() {
            this.dropDownLoading = "bucket";
            this.clearNotices();
            let response;
            try {
                response = await this.getBucketList({ credential: this.selectedCredential });
            } catch (e) {
                this.setError(e);
                this.bucketList = [];
                this.dropDownLoading = "";
                return;
            }
            this.bucketList = normalizeBuckets(response.data);
            this.dropDownLoading = "";
            if (!this.bucketList.length) {
                this.message = `Credential "${this.selectedCredential}" cannot see any buckets.`;
                return;
            }
            if (!this.autoSelectBucket) return;
            const preferred = this.initialBucket && this.bucketList.some((b) => b.name === this.initialBucket)
                ? this.initialBucket
                : this.bucketList[0].name;
            if (this.selectedBucket === preferred) {
                // the watcher will not fire for an unchanged value - re-emit so the parent reloads
                this.$emit("bucketChange", preferred);
            } else {
                this.selectedBucket = preferred;
            }
        },
        onRefresh() {
            if (this.selectedBucket) {
                this.$emit("refresh");
            } else if (this.selectedCredential) {
                this.fetchBuckets();
            } else if (this.selectedNamespace) {
                this.fetchCredentials(true);
            }
        },
    },
    mounted() {
        this.initSelection();
    },
    emits: ["credentialChange", "bucketChange", "refresh"],
};
</script>

<style scoped>
.fb-controls-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
    gap: 1rem;
    align-items: end;
}

@media (max-width: 991.98px) {
    .fb-controls-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .fb-controls-refresh {
        justify-self: start;
    }
}

@media (max-width: 575.98px) {
    .fb-controls-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

.fb-field {
    min-width: 0;
}

.fb-controls :deep(.form-group) {
    margin-bottom: 0;
}

/* The picker sets its own control height, so the button is pinned to match it
   rather than to a line-height that would drift with the font */
.fb-controls-refresh {
    height: 2.5rem;
    width: 2.5rem;
}

.fb-controls :deep(.multiselect) {
    border-radius: var(--fb-radius-sm);
    border-color: var(--fb-border);
    background: var(--fb-surface-2);
    min-height: 2.5rem;
    --ms-ring-color: var(--fb-primary-dim);
}

.fb-controls :deep(.multiselect.is-active) {
    border-color: var(--fb-primary);
}

.fb-controls :deep(.multiselect.is-disabled) {
    background: var(--fb-surface-3);
    opacity: 0.7;
}

.fb-controls :deep(.multiselect-single-label),
.fb-controls :deep(.multiselect-search input) {
    color: var(--fb-text);
    font-size: 13px;
}

.fb-controls :deep(.multiselect-placeholder) {
    color: var(--fb-text-dim);
    font-size: 13px;
}

.fb-controls :deep(.multiselect-dropdown) {
    border-radius: var(--fb-radius-sm);
    border-color: var(--fb-border);
    background: var(--fb-surface);
}

.fb-controls :deep(.multiselect-option) {
    color: var(--fb-text);
    font-size: 13px;
}

.fb-controls :deep(.multiselect-option.is-pointed) {
    background: var(--fb-surface-2);
}

.fb-controls :deep(.multiselect-option.is-selected) {
    background: var(--fb-primary-dim);
    color: var(--fb-primary);
}
</style>
